import { Box, Container, Link, Stack, styled } from '@mui/material';
import SectionTitle from '../../sections/landing/components/SectionTitle';
import SectionDescription from '../../sections/landing/components/SectionDescription';
import CardTitle from '../../sections/landing/components/CardTitle';
import { useTheme } from '@mui/material/styles';

const List = styled('ul')({
  margin: 0,
  padding: 0,
  listStylePosition: 'inside'
});

const termsOfService = [
  {
    title: 'Introduction',
    description: {
      text: 'Welcome to Knacksters (“we,” “our,” or “us”). By accessing or using our platform, you agree to comply with and be bound by these Terms of Service (“Terms”). If you do not agree to these Terms, please do not use our services.'
    }
  },
  {
    title: 'Our Services',
    description: {
      text: 'Knacksters provides businesses and entrepreneurs with personalized support, including but not limited to project management, marketing, design, development, and administrative services. The services are delivered through a managed and flexible framework tailored to client needs.'
    }
  },
  {
    title: 'Eligibility',
    description: {
      list: [
        'You must be at least 18 years old and capable of entering into a legally binding contract.',
        'By using our services, you confirm that all the information provided during registration is accurate and up-to-date.'
      ]
    }
  },
  {
    title: 'User Accounts',
    description: {
      list: [
        'You are responsible for maintaining the confidentiality of your login credentials and for all activities conducted through your account.',
        ['Notify us immediately at ', 'connect@knacksters.co', ' of any unauthorized use of your account.']
      ]
    }
  },
  {
    title: 'Payments and Billing',
    description: {
      list: [
        'Payments for our services are processed securely. All charges are detailed at the time of purchase or subscription.',
        'Failure to make payments on time may result in the suspension or termination of your access to our services.',
        'Refunds are not offered unless explicitly stated or required by law.'
      ]
    }
  },
  {
    title: 'Service Availability',
    description: {
      text: 'We aim to provide uninterrupted access to our platform. However, we may experience downtimes due to maintenance or unforeseen technical issues. We are not liable for any delays, disruptions, or data losses caused by such downtimes.'
    }
  },
  {
    title: 'User Responsibilities',
    description: {
      title: 'When using our platform, you agree not to:',
      list: [
        'Engage in illegal or unauthorized activities.',
        'Share, sell, or sublicense your account to third parties.',
        'Attempt to reverse engineer, hack, or disrupt the platform or its associated systems.'
      ]
    }
  },
  {
    title: 'Intellectual Property',
    description: {
      text: 'All content, trademarks, and intellectual property on Knacksters’ platform are the property of Knacksters or its licensors. You may not reproduce, distribute, or use the content without explicit permission.'
    }
  },
  {
    title: 'Limitation of Liability',
    description: {
      list: [
        'Knacksters is not liable for any indirect, incidental, or consequential damages arising from your use of our services.',
        'Our total liability to you for any claim is limited to the amount you have paid to us in the 12 months preceding the claim.'
      ]
    }
  },
  {
    title: 'Termination',
    description: {
      text: 'We reserve the right to terminate or suspend your account without notice if you breach these Terms or engage in activities that harm Knacksters or its users.'
    }
  },
  {
    title: 'Governing Law',
    description: {
      text: 'These Terms are governed by the laws of England and Wales. Any disputes will be resolved exclusively in the courts of England and Wales.'
    }
  },
  {
    title: 'Contact Information',
    description: {
      text: ['If you have any questions about these Terms, please contact us at ', 'connect@knacksters.co', '.']
    }
  }
];

const TermsOfService = () => {
  const theme = useTheme();

  const renderTextWithLink = (text: string | string[]) => {
    if (Array.isArray(text)) {
      return (
        <>
          {text[0]}
          <Link href={`mailto:${text[1]}`} className="link" underline="none">
            {text[1]}
          </Link>
          {text[2]}
        </>
      );
    }
    return text;
  };

  return (
    <main>
      <Box component="section" py={8}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <SectionTitle pb={4}>Terms of Service</SectionTitle>
            {termsOfService.map((item, index) => (
              <Stack
                gap={1}
                key={index}
                sx={{
                  '& .link': {
                    color: theme.palette.grey[600],

                    '&:hover': {
                      color: theme.palette.primary[900]
                    }
                  }
                }}
              >
                <CardTitle>
                  {index + 1}. {item.title}
                </CardTitle>
                {item.description.title && (
                  <SectionDescription
                    as="p"
                    sx={{
                      margin: 0
                    }}
                  >
                    {item.description.title}
                  </SectionDescription>
                )}
                {item.description.list && (
                  <List>
                    {item.description.list.map((text, index) => (
                      <SectionDescription key={index} as="li">
                        {renderTextWithLink(text)}
                      </SectionDescription>
                    ))}
                  </List>
                )}
                {item.description.text && (
                  <SectionDescription
                    as="p"
                    sx={{
                      margin: 0
                    }}
                  >
                    {renderTextWithLink(item.description.text)}
                  </SectionDescription>
                )}
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>
    </main>
  );
};

export default TermsOfService;
